const yup = require('yup')
const timeField = require('./fields/time-field')

const emptyStringToNull = (v, o) => (o === '' ? null : v)

const statValueSchema = ({ allowString = false, valueLabel, required = false } = {}) => {
  let value = allowString ? yup.string().ensure() : yup.number().min(0).transform(emptyStringToNull).nullable()
  if (required) {
    value = value.required().label(valueLabel)
  }

  return yup.object().shape({
    value,
    stat: yup.mixed().nullable(),
  })
}

const openTotalSchema = ({ allowString = false, openRequired = false, totalRequired = false } = {}) =>
  yup.object().shape({
    open: statValueSchema({ allowString, valueLabel: 'Open', required: openRequired }),
    total: statValueSchema({ allowString, valueLabel: 'Total', required: totalRequired }),
  })

const shape = ({ allowString = false }) => {
  const schema = yup.object().shape({
    reportDate: yup.date(),
    updateLinkedStats: yup.string().oneOf(['static', 'dynamic']).default('dynamic'),
    operatingStatus: yup.string().ensure().required().label('Operating Status'),
    comments: yup.string().ensure(),
    commentsLinked: yup.boolean().default(false),
    seasonTotal: statValueSchema({ allowString }),
    latestSnowfall: yup.object().shape({
      daysAgo0: statValueSchema({ allowString }),
      daysAgo1: statValueSchema({ allowString }),
      daysAgo2: statValueSchema({ allowString }),
      daysAgo3: statValueSchema({ allowString }),
      daysAgo4: statValueSchema({ allowString }),
    }),
    baseDepth: yup.object().shape({
      min: statValueSchema({ allowString }),
      max: statValueSchema({ allowString }),
    }),
    snowDepth: yup.object().shape({
      base: statValueSchema({ allowString }),
      mid: statValueSchema({ allowString }),
      summit: statValueSchema({ allowString }),
    }),
    surfaceConditions: yup.object().shape({
      primary: yup.string().ensure(),
      secondary: yup.string().ensure().when('primary', {
        is: Boolean,
        then: (s) => s.notOneOf([yup.ref('primary')], 'Surface conditions cannot be the same.'),
      }),
      nightGroomingActive: yup.boolean().default(false),
      nightGroomingCount: yup.number().default(0),
      nightGroomingPercentage: yup.number().min(0).max(100).default(0),
    }).when('publishTo.snoCountry', (v, s) => {
      return !v ? s : s.shape({
        nightGroomingPercentage: yup.number().min(0).max(100).default(0).when('primary', {
          is: 'Machine Groomed',
          then: (s) => s.min(50, 'Must be at least 50% when primary conditions are Machine Groomed.'),
        }).when('secondary', {
          is: 'Machine Groomed',
          then: (s) => s.max(50, 'Must be less than 50% when secondary conditions are Machine Groomed.'),
        }),
      })
    }),
    lifts: yup.object().when('publishTo', (value) => {
      const openRequired = value && value.onTheSnow
      const totalRequired = value && value.onTheSnow
      return openTotalSchema({ allowString, openRequired, totalRequired })
    }),
    trails: openTotalSchema({ allowString }),
    terrainAcres: openTotalSchema({ allowString }),
    terrainLength: openTotalSchema({ allowString }),
    tubing: yup.object().shape({
      active: yup.boolean().default(false),
      hours: yup.string(),
      lanesCount: yup.number().min(0),
    }),
    nightSkiing: yup.object().shape({
      active: yup.boolean().default(false),
      trailCount: yup.number().min(0),
      liftCount: yup.number().min(0),
    }),
    snowMaking: yup.object().shape({
      active: yup.boolean().default(false),
      trailCount: yup.number().min(0),
      trailPercentage: yup.number().min(0).max(100),
    }),
    weekdayHours: yup.array().of(
      yup.object().shape({
        days: yup.array().of(yup.number().min(0).max(6)).min(1),
        start: timeField(),
        end: timeField(),
      })
    ).default([]),
    weekendHours: yup.array().of(
      yup.object().shape({
        days: yup.array().of(yup.number().min(0).max(6)).min(1),
        start: timeField(),
        end: timeField(),
      })
    ).default([]),
    specialHours: yup.array().of(
      yup.object().shape({
        date: yup.mixed().datetime().required(),
        closed: yup.boolean(),
        start: timeField(),
        end: timeField(),
      })
    ).default([]),
    publishTo: yup.object().shape({
      onTheSnow: yup.boolean().default(false),
      snoCountry: yup.boolean().default(false),
    }),
    reportStatus: yup.string().oneOf(['pending', 'publishing', 'succeeded', 'failed']).default('pending'),
    publishStatus: yup.object().shape({
      onTheSnow: yup.string().oneOf(['pending', 'publishing', 'succeeded', 'failed']).default('pending'),
      snoCountry: yup.string().oneOf(['pending', 'publishing', 'succeeded', 'failed']).default('pending'),
    }),
    createdAt: yup.date(),
    updatedAt: yup.date(),
    createdBy: yup.mixed(),
    updatedBy: yup.mixed(),
  })

  return schema
}

const snowReportSchemaPartial = shape({ allowString: false })
const snowReportSchema = snowReportSchemaPartial
const snowReportFormSchema = shape({ allowString: true }).shape({
  reportDate: yup.mixed().datetime(),
})
const snowReportResponseSchema = snowReportSchema.shape({
  result: yup.object().shape({
    onTheSnow: yup.object().shape({ errorMessage: yup.string().ensure() }).default({}),
    snoCountry: yup.object().shape({ errorMessage: yup.string().ensure() }).default({}),
  }),
})

module.exports = { snowReportSchema, snowReportSchemaPartial, snowReportFormSchema, snowReportResponseSchema }
